import { format } from 'date-fns'

export const prettyDate = (date: string | number | Date, format?: 'userLocalTime' | 'utc' | 'humanReadable' | 'humanReadableDay') => {
  if (!date) return 'Unknown'

  if (format === undefined) {
    return new Date(date).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const datetime = new Date(date).toLocaleString('en-GB', {
    ...(format === 'utc' && { timeZone: 'UTC' }),
    minute: 'numeric',
    timeZoneName: 'short',
    hour: 'numeric',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })

  if (format === 'userLocalTime') {
    return datetime
  }

  if (format === 'humanReadable') {
    const now = new Date()
    const inputDate = new Date(date)
    const diffInSeconds = Math.floor((now.getTime() - inputDate.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return 'just now'
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`
    }
  }

  if (format === 'humanReadableDay') return formatDateWithTodayAndYesterday(date.toString())

  return datetime
}

export const calculatePreviousDateByMinutes = (minutes: number) => {
  const currentDate = new Date()
  currentDate.setMinutes(currentDate.getMinutes() - minutes)
  return currentDate
}

export const dateToLocalisedISOString = (date: Date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
}

export const timeInHoursAndMinutes = (seconds: number) => {
  // Check if seconds is a number and is not NaN
  if (typeof seconds !== 'number' || isNaN(seconds)) {
    return '-'
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hours * 3600) / 60)
  return `${hours}h ${minutes}m`
}

export const timeInDaysHoursAndMinutes = (seconds: number) => {
  if (!seconds) return 'Unknown'

  const days = Math.floor(seconds / (3600 * 24))
  const hours = Math.floor((seconds - days * 3600 * 24) / 3600)
  const minutes = Math.floor((seconds - days * 3600 * 24 - hours * 3600) / 60)

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  } else {
    return `${hours}h ${minutes}m`
  }
}

/*
 * Transforms date to hours ago if date doesn't exist
 */
export const transformDateToHoursAgo = (date: Date, hours: number) => {
  const newDate = new Date(date)
  newDate.setHours(newDate.getHours() - hours)
  return newDate.toISOString()
}

export const formatToDateTimeLocal = (isoString: string) => {
  // Create a new Date object using the ISO string
  const date = new Date(isoString)

  // Convert to YYYY-MM-DDTHH:mm format
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0') // getMonth() is zero-indexed
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Function converts a period (e.g., '1m') to a date string, based on
 * the time period from the current date.
 * @param period string period, e.g., 1m, 2w, 1y...
 * @returns string-formatted date since the period
 */
type PeriodUnit = 'w' | 'm' | 'y'
type Period = `${number}${PeriodUnit}` | PeriodUnit

export const getDateSincePeriod = (period: Period): string => {
  const match = period.match(/^(\d*)([wmy])$/)
  if (!match) throw new Error('Invalid period format. Use "w", "m", "y", "3w", "6m", etc.')

  const [, count = '1', unit] = match
  const periodCount = parseInt(count, 10)

  const currentDate = new Date()
  const resultDate = new Date(currentDate)

  switch (unit as PeriodUnit) {
    case 'w':
      resultDate.setDate(currentDate.getDate() - periodCount * 7)
      break
    case 'm':
      resultDate.setMonth(currentDate.getMonth() - periodCount)
      break
    case 'y':
      resultDate.setFullYear(currentDate.getFullYear() - periodCount)
      break
  }

  return resultDate.toISOString().split('T')[0] + 'T00:00'
}

const formatDateWithTodayAndYesterday = (date: string) => {
  if (format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')) return 'Today'
  if (format(date, 'yyyy-MM-dd') === format(new Date(new Date().setDate(new Date().getDate() - 1)), 'yyyy-MM-dd')) return 'Yesterday'
  return format(date, 'yyyy-MM-dd')
}

export const timeBetweenDates = (startDate: Date | string | undefined, endDate: Date | string = new Date()) => {
  if (!startDate) return { seconds: 0, minutes: 0, hours: 0, days: 0 }
  const start = new Date(startDate)
  const end = new Date(endDate)

  const diffInMs = end.getTime() - start.getTime()

  const seconds = Math.floor(diffInMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  return { seconds, minutes, hours, days }
}
